import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Tab {
  name: string;
  slug: string;
  tabColor?: string;
  tabTextColor?: string;
  content: React.ReactNode;
}

interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  onTabChange?: (index: string) => void;
  className?: string;
}

function getPreferredTabSlug(tabs: Tab[], defaultTab?: string): string {
  if (tabs.length === 0) return '';
  const hasPbm = tabs.some((t) => t.slug === 'pbm');
  if (hasPbm) return 'pbm';
  if (defaultTab && tabs.some((t) => t.slug === defaultTab)) return defaultTab;
  return tabs[0].slug;
}

export default function TabsPanel({
  tabs,
  defaultTab,
  onTabChange,
  className = '',
}: Readonly<TabsProps>) {
  const preferredSlug = getPreferredTabSlug(tabs, defaultTab);
  const [activeTab, setActiveTab] = useState(() => preferredSlug || defaultTab);
  const prevTabSlugsRef = useRef<string[]>([]);

  const handleTabChange = (index: string) => {
    setActiveTab(index);
    if (onTabChange) {
      onTabChange(index);
    }
  };

  useEffect(() => {
    if (tabs.length === 0) return;
    const currentSlugs = tabs.map((t) => t.slug);
    const hadPbm = prevTabSlugsRef.current.includes('pbm');
    const hasPbm = currentSlugs.includes('pbm');
    const pbmJustAppeared = hasPbm && !hadPbm;
    prevTabSlugsRef.current = currentSlugs;

    const preferred = getPreferredTabSlug(tabs, defaultTab);
    const currentValid = tabs.some((t) => t.slug === activeTab);

    if (!currentValid) {
      setActiveTab(preferred);
    } else if (pbmJustAppeared) {
      setActiveTab('pbm');
    }
  }, [tabs, activeTab, defaultTab]);

  const selectedTab = tabs.find((tab) => tab.slug === activeTab);

  return (
    <div className={`w-full py-6 ${className}`}>
      {/* Tab headers - only show when there is more than one tab */}
      {tabs.length > 1 && (
        <div className="flex space-x-2">
          {tabs.map((tab, index) => (
            <button
              key={index}
              type="button"
              data-tab-slug={tab.slug}
              onClick={() => handleTabChange(tab.slug)}
              className={`py-2 px-4 text-sm rounded-full ${tab.tabColor} ${
                tab.tabTextColor
              } focus:outline-none ${
                activeTab === tab.slug
                  ? 'border border-gray-200'
                  : 'text-gray-600 hover:border hover:border-gray-200'
              } transition duration-300 ease-in-out`}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {/* Tab content with motion transition */}
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="pt-6"
          >
            {selectedTab?.content}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
